import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";

import {
  useFormatCurrency,
  useGetCurrentLoanApplicationID,
} from "@krea/customer-features/hooks";
import ArrowSwitchSVG from "@krea/customer-features/images/svg/switch-arrows.svg";
import { settings } from "@krea/customer-features/settings";
import ExpandAnimation from "@krea/customer-features/shared-components/animations/Expand";
import BidDetailsItem from "@krea/customer-features/shared-components/bid-details/BidDetailsItem";
import BidDetailsList from "@krea/customer-features/shared-components/bid-details/BidDetailsList";
import { Text } from "@krea/customer-features/shared-components/text";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { useChangeCustomerPreferredRate } from "@krea/customer-features/store/current-loan-application/hooks/useChangeCustomerPreferredRate";
import { IInvoicePurchaseBid } from "@krea/customer-features/types/lenderResponse";
import { RATE_TO_BID_KEY_MAP } from "@krea/customer-features/types/loanApplication";

const FactoringBidDetails = ({
  className,
  bid,
  withAnimation,
  isPostSelect = false,
}: {
  className?: string;
  bid: IInvoicePurchaseBid;
  withAnimation?: boolean;
  isPostSelect?: boolean;
}) => {
  const { t } = useTranslation();

  const currency = settings.currency;
  const [formattedCurrencyValue] = useFormatCurrency({ currency });

  const { data } = useGetCachedCurrentLoanApplication();
  const { customerPreferredRate, alternativeRate } = data ?? {};

  const rateKey = RATE_TO_BID_KEY_MAP[customerPreferredRate!];

  const loanApplicationID = useGetCurrentLoanApplicationID();
  const { mutate: changeCustomerPreferredRate } =
    useChangeCustomerPreferredRate(loanApplicationID!);

  const content = (
    <BidDetailsList className={className}>
      {isPostSelect && (
        <>
          <BidDetailsItem
            title={t("dash.factoringBids.columns.approvedAmount")}
            value={formattedCurrencyValue(bid.purchasedInvoiceAmount)}
          />

          <BidDetailsItem
            title={t("dash.factoringBids.columns.commissionTime")}
            value={`${bid.commissionPercent}%`}
          />

          <BidDetailsItem
            title={t("dash.factoringBids.columns.regressCost")}
            value={
              bid.withRegress
                ? t("dash.factoringBids.withRegressYes")
                : t("dash.factoringBids.withRegressNo")
            }
          />
        </>
      )}

      <BidDetailsItem
        title={t("factoringBidDetails.purchasedAmountPercent")}
        value={`${bid.purchasedAmountPercent}%`}
      />

      {bid.purchaseLimit ? (
        <BidDetailsItem
          title={t("factoringBidDetails.purchaseLimit")}
          value={formattedCurrencyValue(bid.purchaseLimit)}
        />
      ) : null}

      {bid.limitFeePerYearPercent ? (
        <BidDetailsItem
          title={t("factoringBidDetails.limitFeePerYearPercent")}
          value={`${bid.limitFeePerYearPercent}%`}
        />
      ) : null}

      <BidDetailsItem
        title={t("factoringBidDetails.setupFee")}
        value={
          bid.setupFee
            ? formattedCurrencyValue(bid.setupFee)
            : t("bidDetails.repayableWithoutFee")
        }
      />

      {bid.handlingFeePerInvoice ? (
        <BidDetailsItem
          title={t("factoringBidDetails.handlingFeePerInvoice")}
          value={formattedCurrencyValue(bid.handlingFeePerInvoice)}
        />
      ) : null}

      {bid.monthlyFee ? (
        <BidDetailsItem
          title={t("factoringBidDetails.monthlyFee")}
          value={formattedCurrencyValue(bid.monthlyFee)}
        />
      ) : null}

      {/* <BidDetailsItem
        title={t('factoringBidDetails.totalMonthlyCost')}
        value={formattedCurrencyValue(bid.totalMonthlyCost)}
      /> */}

      {bid.regressNumDaysAfterDueDate ? (
        <BidDetailsItem
          title={t("factoringBidDetails.regressNumDaysAfterDueDate")}
          value={`${bid.regressNumDaysAfterDueDate} ${t("factoringBidDetails.regressDaysFormat")}`}
        />
      ) : null}

      {/* <Text size="sm" className="font-weight-bold text-gray-dark">
        {t('factoringBidDetails.totalCostExplainer')}
      </Text> */}

      {bid[rateKey] ? (
        <>
          <hr className="tw-m-0 tw-mb-[16px] tw-border-t-2 tw-border-dotted" />
          <div className="md:tw-flex md:tw-justify-between">
            <Text size="md" className="tw-font-bold tw-text-gray-600">
              {t("bidDetails.keyCalculations")}{" "}
            </Text>

            {alternativeRate && (
              <div
                className="tw-px-2 tw-flex tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-border-primary tw-bg-primary-150 tw-py-1 tw-align-middle"
                onClick={() => changeCustomerPreferredRate()}
              >
                <div className="tw-mr-3 tw-flex tw-align-middle">
                  <div className="tw-relative tw-mr-2 tw-block tw-w-[16px] before:tw-p-0 before:tw-pb-[100%]">
                    <SVG
                      className="tw-absolute tw-left-0 tw-top-0 tw-block tw-h-full tw-w-full"
                      src={ArrowSwitchSVG}
                    />
                  </div>
                  <div className="tw-text-nowrap tw-text-sm tw-font-semibold tw-leading-normal tw-text-primary">
                    {t(
                      `dash.bidsRateToaster.${RATE_TO_BID_KEY_MAP[alternativeRate]}.showText`,
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <BidDetailsItem
            title={t(`dash.commonBidsColumns.${rateKey}.title`)}
            value={`${bid[rateKey]}%`}
          />

          <BidDetailsItem
            title={
              rateKey === "firstMonthInterestRate"
                ? t("bidDetails.firstMonthCost")
                : t("bidDetails.averageMonthlyCost")
            }
            value={
              rateKey === "firstMonthInterestRate"
                ? formattedCurrencyValue(bid.firstMonthCost)
                : formattedCurrencyValue(bid.totalMonthlyCost)
            }
          />
        </>
      ) : (
        <></>
      )}
    </BidDetailsList>
  );

  return withAnimation ? (
    <ExpandAnimation className="">{content}</ExpandAnimation>
  ) : (
    <>{content}</>
  );
};

export default FactoringBidDetails;
